import { FormInput, RadioGroup, RadioGroupDataInterface } from '@systemeio/ui'
import { BadRequest } from '@systemeio/util'
import { LectureInterface } from 'modules/courses/courses/curriculum/types/lecture-interface'
import { createLectureEmail } from 'modules/courses/emails/api/lecture-email-api'
import { LectureEmailCreateInterface } from 'modules/courses/emails/types/lecture-email-inteface'
import EmailTemplateSelector from 'modules/email/components/email-template-selector/email-template-selector'
import { useGetEmailTemplates } from 'modules/email/hooks/use-get-email-templates'
import { CreateEmailInterface } from 'modules/mailing/types/mailing-interface'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import CreateModal, { CreateModalProps } from 'shared/components/create-modal'
import { useEmailOptions } from 'shared/components/dashboard-mail-editor/hooks/use-email-options'
import SenderEmailSelect from 'shared/components/sender-email-select'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useUpdateLectureEmail } from '../../../../emails/hooks/use-update-lecture-email'
import { CourseId } from '../../../types/course-interface'
import { useUpdateLectureData } from '../../hooks/use-update-lecture-data'
import { CourseLectureEmailErrorResponseContentInterface } from '../../types/course-lecture-email-error-response-interface'
import { getCourseLectureEmailUrl } from '../../utils/get-course-lecture-email-url'

const defaultErrors: CreateModuleErrorsInterface = {
  fromEmail: '',
  fromName: '',
  subject: '',
  useNewEditor: '',
  template: '',
}

const defaultValues: CreateEmailInterface = {
  fromEmail: '',
  fromName: '',
  subject: '',
  useNewEditor: true,
  template: undefined,
}

export type CreateModuleErrorsInterface = Record<keyof CreateEmailInterface, string>

interface LectureEmailActionProps extends Pick<CreateModalProps<CreateEmailInterface>, 'onClose'> {
  lecture: LectureInterface
  isOpened: boolean
}

export function LectureEmailAction({ lecture, isOpened, onClose }: LectureEmailActionProps) {
  const { t } = useLocoTranslation()
  const router = useRouter()
  const courseId = Number(router.query.courseId) as CourseId

  const [errors, setErrors] = useState(defaultErrors)
  const [values, setValues] = useState(defaultValues)

  const { data: options } = useEmailOptions()

  const { templates } = useGetEmailTemplates()

  const templateTypeData: RadioGroupDataInterface<string>[] = [
    { id: 'new', caption: t('dashboard.email_template_selector.template_visual_editor') },
    { id: 'old', caption: t('dashboard.email_template_selector.template_classic_editor') },
  ]

  const updateLectureData = useUpdateLectureData(courseId)
  const updateLectureEmail = useUpdateLectureEmail(lecture.id)

  const afterLeave = () => {
    setErrors(defaultErrors)
  }

  const onCreate = async (data: CreateEmailInterface) => {
    try {
      const createData: LectureEmailCreateInterface = {
        drippingAccessMailing: {
          currentEmail: {
            subject: data.subject,
            fromName: data.fromName,
            fromEmail: data.fromEmail,
            useNewEditor: data.useNewEditor,
            template: data.template,
          },
        },
      }
      await createLectureEmail(lecture.id, createData)
      await updateLectureData(false)
      await updateLectureEmail(false)
      router.push(
        getCourseLectureEmailUrl({
          courseId,
          lectureId: lecture.id,
        }),
      )
    } catch (e) {
      if (e instanceof BadRequest) {
        const errors = e.errors as CourseLectureEmailErrorResponseContentInterface
        const fields = errors.fields
        if (!Array.isArray(fields)) {
          const emailErrors = fields.drippingAccessMailing?.currentEmail
          setErrors(prev => ({
            ...prev,
            fromEmail: emailErrors?.fromEmail?.join('') || '',
            fromName: emailErrors?.fromName?.join('') || '',
            subject: emailErrors?.subject?.join('') || '',
            template: emailErrors?.template?.join('') || '',
          }))
        }
        if (e.errors.common) {
          const commonErrors = e.errors.common
          setErrors(prev => {
            return {
              ...prev,
              common: commonErrors,
            }
          })
        }
      }
      throw e
    }
  }

  useEffect(() => {
    if (options) {
      setValues(prev => ({
        ...prev,
        fromEmail: prev.fromEmail || options?.senderEmailAddress,
        fromName: prev.fromName || options?.senderName,
      }))
    }
  }, [options])

  useEffect(() => {
    if (templates) {
      setValues(prev => ({
        ...prev,
        template: templates?.[0].id,
      }))
    }
  }, [templates])

  return (
    <CreateModal
      opened={isOpened}
      isPrefetching={!options}
      onClose={onClose}
      afterLeave={afterLeave}
      defaultValues={values}
      caption={t('global.email')}
      requiredFields={['subject', 'fromName', 'fromEmail']}
      creatable={[
        {
          field: 'subject',
          onRender: (value, onChange) => (
            <FormInput
              required
              key="newsletter-subject"
              label={t('dashboard.email.subject')}
              value={value}
              onChange={e => onChange(e.target.value)}
              maxLength={256}
              error={errors.subject}
            />
          ),
        },
        {
          field: 'fromName',
          onRender: (value, onChange) => (
            <FormInput
              required
              key="sender-name"
              label={t('dashboard.email.sender_name')}
              value={value}
              onChange={e => onChange(e.target.value)}
              maxLength={256}
              error={errors.fromName}
            />
          ),
        },
        {
          field: 'fromEmail',
          onRender: (value, onChange) => (
            <SenderEmailSelect
              required
              key="sender-email"
              isPreFetching={!options}
              onChange={data => data && onChange(data)}
              value={value}
              error={errors.fromEmail}
            />
          ),
        },
        {
          field: 'useNewEditor',
          onRender: (value, onChange, state, onChangeState) => (
            <RadioGroup
              key="editor-type"
              options={templateTypeData}
              selected={value ? 'new' : 'old'}
              onChange={value => {
                if (value === 'old') {
                  onChangeState({ ...state, useNewEditor: false, template: undefined })
                } else onChangeState({ ...state, useNewEditor: true, template: templates?.[0].id })
              }}
            />
          ),
        },
        {
          field: 'template',
          shouldRender: (state: CreateEmailInterface) => state.useNewEditor,
          onRender: (value, onChange) => (
            <EmailTemplateSelector
              key="template"
              onChange={onChange}
              templateId={value}
              templates={templates}
            />
          ),
        },
      ]}
      onCreate={data => onCreate({ ...values, ...data })}
    />
  )
}
